import { useState } from 'react';
import Layout from '@/components/base/Layout';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo, aboutInfo, stats, contactInfo } from '@/mocks/data';

function AboutSEO() {
  return (
    <SEO
      title={`关于我们 - ${siteInfo.name} | 品牌介绍 荣誉资质 联系方式`}
      description={`${siteInfo.name}成立于2015年，是国内领先的体育综合服务平台。了解满冠体育的品牌故事、发展历程、核心价值观和联系方式。`}
      keywords="满冠体育,关于满冠体育,体育平台介绍,体育品牌,联系方式,荣誉资质"
      canonicalUrl={`${siteInfo.url}/about`}
      structuredData={{
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Organization',
            name: siteInfo.name,
            url: siteInfo.url,
            description: aboutInfo.intro,
            foundingDate: '2015',
            address: {
              '@type': 'PostalAddress',
              streetAddress: contactInfo.address,
              addressCountry: 'CN',
            },
            contactPoint: [
              {
                '@type': 'ContactPoint',
                telephone: contactInfo.phone,
                email: contactInfo.email,
                contactType: 'customer service',
                availableLanguage: 'Chinese',
              },
            ],
            sameAs: [
              'https://weibo.com/manguansports',
              'https://www.douyin.com/user/manguansports',
            ],
          },
          {
            '@type': 'WebPage',
            name: '关于我们 - 满冠体育',
            url: `${siteInfo.url}/about`,
            description: '了解满冠体育的品牌故事与联系方式',
            isPartOf: { '@id': `${siteInfo.url}#website` },
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
              { '@type': 'ListItem', position: 2, name: '关于我们', item: `${siteInfo.url}/about` },
            ],
          },
        ],
      }}
    />
  );
}

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9s8o94nlsngrm4lmi90';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('company_alt')?.toString().trim() || '';
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快与您联系！');
      return;
    }

    setFormStatus('submitting');
    setFormMessage('');

    try {
      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快与您联系！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (typeof serverMsg === 'string' && serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后重试。');
        } else {
          setFormStatus('error');
          setFormMessage(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  return (
    <Layout>
      <AboutSEO />

      <div className="pt-16 md:pt-18">
        <div className="bg-background-100">
          <div className="container-custom py-8 md:py-12">
            <Breadcrumb items={[{ label: '关于我们' }]} />
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mb-3">
              关于我们
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
              了解满冠体育的品牌故事、核心价值与发展历程
            </p>
          </div>
        </div>

        <section className="container-custom py-10 md:py-14">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h2 className="text-xl md:text-3xl font-bold text-foreground-900 font-heading mb-4">
                品牌介绍
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                {aboutInfo.intro}
              </p>
              <div className="space-y-3 mt-6">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-eye-line text-primary-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-foreground-900">我们的愿景</h3>
                    <p className="text-xs md:text-sm text-foreground-600">{aboutInfo.vision}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-accent-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-flag-line text-accent-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-foreground-900">我们的使命</h3>
                    <p className="text-xs md:text-sm text-foreground-600">{aboutInfo.mission}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative h-64 md:h-80 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20sports%20office%20headquarters%20with%20professional%20team%20meeting%2C%20warm%20golden%20and%20crimson%20interior%20design%2C%20trophy%20display%20cabinet%2C%20clean%20minimalist%20corporate%20aesthetic%2C%20bright%20natural%20lighting%2C%20professional%20corporate%20photography&width=800&height=600&seq=manguan-about-intro&orientation=landscape"
                alt="满冠体育办公环境"
                className="w-full h-full object-cover rounded-lg"
              />
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="container-custom">
            <h2 className="text-xl md:text-3xl font-bold text-foreground-900 text-center font-heading mb-8">
              核心价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {aboutInfo.values.map((v, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg p-5 md:p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 bg-primary-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-primary-500 font-heading">{idx + 1}</span>
                  </div>
                  <h3 className="text-base font-bold text-foreground-900 mb-2 font-heading">{v.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-600">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="container-custom py-10 md:py-14">
          <h2 className="text-xl md:text-3xl font-bold text-foreground-900 text-center font-heading mb-8">
            发展历程
          </h2>
          <div className="relative max-w-2xl mx-auto">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 transform md:-translate-x-0.5" />
            {aboutInfo.milestones.map((m, idx) => (
              <div key={idx} className={`relative flex items-start gap-6 mb-8 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-primary-500 rounded-full transform -translate-x-1/2 mt-1.5 z-10" />
                <div className={`ml-10 md:ml-0 md:w-1/2 ${idx % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                  <span className="text-lg md:text-xl font-bold text-primary-500 font-heading">{m.year}</span>
                  <p className="text-xs md:text-sm text-foreground-700 mt-1">{m.event}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="container-custom">
            <h2 className="text-xl md:text-3xl font-bold text-foreground-900 text-center font-heading mb-3">
              荣誉资质
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-8">行业认可，品质见证</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {aboutInfo.honors.map((h, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-background-50 rounded-lg p-4">
                  <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-medal-line text-accent-500 text-lg"></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-900">{h}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="container-custom py-10 md:py-14">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            <div>
              <h2 className="text-xl md:text-3xl font-bold text-foreground-900 font-heading mb-6">
                联系我们
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-primary-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-foreground-900 mb-0.5">公司地址</h3>
                    <p className="text-xs md:text-sm text-foreground-600">{contactInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-primary-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-foreground-900 mb-0.5">客服电话</h3>
                    <p className="text-xs md:text-sm text-foreground-600">{contactInfo.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-foreground-900 mb-0.5">电子邮箱</h3>
                    <p className="text-xs md:text-sm text-foreground-600">{contactInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-primary-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-foreground-900 mb-0.5">工作时间</h3>
                    <p className="text-xs md:text-sm text-foreground-600">{contactInfo.workingHours}</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-center gap-3">
                <span className="text-xs text-foreground-600">关注我们：</span>
                <a href="#" className="w-8 h-8 rounded-lg bg-background-200 flex items-center justify-center hover:bg-primary-100 transition-colors" aria-label="微信">
                  <i className="ri-wechat-line text-foreground-700"></i>
                </a>
                <a href="#" className="w-8 h-8 rounded-lg bg-background-200 flex items-center justify-center hover:bg-primary-100 transition-colors" aria-label="微博">
                  <i className="ri-weibo-line text-foreground-700"></i>
                </a>
                <a href="#" className="w-8 h-8 rounded-lg bg-background-200 flex items-center justify-center hover:bg-primary-100 transition-colors" aria-label="抖音">
                  <i className="ri-tiktok-line text-foreground-700"></i>
                </a>
              </div>
            </div>

            <div className="bg-background-100 rounded-lg p-6 md:p-8">
              <h3 className="text-lg md:text-xl font-bold text-foreground-900 font-heading mb-4">
                在线留言
              </h3>
              <form onSubmit={handleSubmit} data-readdy-form>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-xs font-medium text-foreground-700 mb-1.5">电话</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="w-full px-3 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all"
                      placeholder="请输入您的电话"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-xs font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-3 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    />
                  </div>
                  <input
                    type="text"
                    name="company_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? '提交中...' : '提交留言'}
                  </button>

                  {formStatus === 'success' && (
                    <div className="p-3 bg-secondary-100 text-secondary-700 rounded-lg text-sm text-center">
                      <i className="ri-check-line mr-1"></i>{formMessage}
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm text-center">
                      <i className="ri-error-warning-line mr-1"></i>{formMessage}
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}